import { lazy } from 'react';
import { logEvent, getActiveNavCorrelationId, createCorrelationId } from './observability';
import { classifyLatency } from './observability/perfThresholds';

/**
 * Wrapper around React.lazy that retries once on chunk load failure
 * AND emits chunk-load lifecycle traces so route stalls can be
 * decomposed into "slow chunk" vs. "slow first data" post-hoc.
 *
 * @param importFn - dynamic import callable
 * @param viewName - friendly name for traces (defaults to 'unknown'). Pass
 *   the same name as the VIEW_REGISTRY key so traces are groupable.
 */
export default function lazyWithRetry(importFn, viewName = 'unknown') {
  return lazy(() => {
    const correlationId = createCorrelationId();
    const start = performance.now();
    logEvent('view_chunk_trace', 'view_chunk_load_started', {
      phase_tag: 'phase_a1',
      correlationId,
      nav_correlationId: getActiveNavCorrelationId(),
      view_name: viewName,
    });

    const onSuccess = (mod, retried) => {
      const latency_ms = Math.round(performance.now() - start);
      logEvent('view_chunk_trace', 'view_chunk_load_completed', {
        phase_tag: 'phase_a1',
        correlationId,
        nav_correlationId: getActiveNavCorrelationId(),
        view_name: viewName,
        latency_ms,
        classification: classifyLatency(latency_ms, 'query'),
        retried,
      });
      return mod;
    };

    const onFailure = (err) => {
      const latency_ms = Math.round(performance.now() - start);
      logEvent('view_chunk_trace', 'view_chunk_load_failed', {
        phase_tag: 'phase_a1',
        correlationId,
        nav_correlationId: getActiveNavCorrelationId(),
        view_name: viewName,
        latency_ms,
        error_message: String(err?.message || err || 'unknown').slice(0, 200),
      });
      throw err;
    };

    return importFn().then(
      (mod) => onSuccess(mod, false),
      (err) => {
        const msg = err?.message || '';
        const isChunkError =
          msg.includes('Failed to fetch dynamically imported module') ||
          msg.includes('is not a valid JavaScript MIME type') ||
          msg.includes('Loading chunk') ||
          msg.includes('ChunkLoadError');

        if (isChunkError) {
          console.warn('[lazyWithRetry] Chunk load failed, retrying...', msg);
          return new Promise((resolve) => setTimeout(resolve, 100))
            .then(() => importFn())
            .then((mod) => onSuccess(mod, true), onFailure);
        }
        return onFailure(err);
      },
    );
  });
}
